/**
 * The attachments block on the note page: image thumbnails, a file list with
 * preview / download / remove, and an upload control that also takes drops.
 *
 * Files are stored in Dropbox and proxied by the server. The URLs on each
 * attachment are signed and self-authorising, which is what lets a plain
 * `<img src>` and a `target="_blank"` link work — neither can carry the bearer
 * header that every other call in this app depends on.
 */

import { apiUrl } from "../api/config";
import { deleteAttachment, fetchAttachments, uploadAttachment } from "../api/notes";
import type { Attachment, AttachmentList } from "../api/types";
import { clear, el } from "../lib/dom";
import { downloadIcon, trashIcon } from "../lib/icons";
import { describeError, isAbort } from "../lib/errors";

export interface AttachmentsSection {
  readonly element: HTMLElement;
  readonly dispose: () => void;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

function imageGallery(images: readonly Attachment[]): HTMLElement {
  return el("ul", {
    className: "attachments__gallery",
    children: images.map((image) =>
      el("li", {
        children: [
          // Opens the full-size file in its own tab. `noopener` so the tab
          // cannot reach back into this one via window.opener.
          el("a", {
            attrs: { href: apiUrl(image.previewUrl), target: "_blank", rel: "noopener", title: image.filename },
            children: [
              el("img", {
                className: "attachments__image",
                attrs: { src: apiUrl(image.previewUrl), alt: image.filename, loading: "lazy" },
              }),
            ],
          }),
        ],
      }),
    ),
  });
}

function fileRow(attachment: Attachment, onRemove: (a: Attachment) => void, removing: boolean): HTMLElement {
  const label = `${attachment.filename}`;
  return el("li", {
    className: "attachments__row",
    children: [
      attachment.previewable
        ? el("a", {
            className: "attachments__name",
            attrs: { href: apiUrl(attachment.previewUrl), target: "_blank", rel: "noopener" },
            text: label,
          })
        : el("span", { className: "attachments__name", text: label }),
      el("span", { className: "attachments__size", text: formatBytes(attachment.size) }),
      el("a", {
        className: "button button--secondary button--icon",
        attrs: {
          href: apiUrl(attachment.downloadUrl),
          download: attachment.filename,
          title: "Download",
          "aria-label": `Download ${attachment.filename}`,
        },
        children: [downloadIcon()],
      }),
      el("button", {
        className: "button button--ghost button--icon attachments__remove",
        attrs: { type: "button", title: "Remove", "aria-label": `Remove ${attachment.filename}`, disabled: removing },
        children: [trashIcon()],
        on: { click: () => onRemove(attachment) },
      }),
    ],
  });
}

export function attachmentsSection(noteId: string): AttachmentsSection {
  const body = el("div", { className: "attachments__body" });
  const status = el("p", { className: "attachments__status", attrs: { role: "status", "aria-live": "polite" } });
  const fileInput = el("input", { attrs: { type: "file", multiple: true, hidden: true } });
  const addButton = el("button", {
    className: "button button--secondary",
    attrs: { type: "button" },
    text: "Add files",
    on: { click: () => fileInput.click() },
  });
  const element = el("section", {
    className: "attachments",
    attrs: { "aria-label": "Attachments" },
    children: [
      el("div", {
        className: "attachments__head",
        children: [el("h2", { className: "note__aside-title", text: "Attachments" }), addButton, fileInput],
      }),
      status,
      body,
    ],
  });

  let controller = new AbortController();
  let disposed = false;
  let current: AttachmentList | null = null;
  let busy = false;

  const setStatus = (message: string, isError = false): void => {
    status.textContent = message;
    status.classList.toggle("attachments__status--error", isError);
  };

  const render = (list: AttachmentList): void => {
    current = list;
    clear(body);
    addButton.hidden = !list.enabled;
    if (!list.enabled) {
      body.appendChild(
        el("p", {
          className: "state__detail",
          text: "Attachments are not set up on this server (no Dropbox credentials).",
        }),
      );
      return;
    }
    if (list.attachments.length === 0) {
      body.appendChild(el("p", { className: "state__detail", text: "No attachments. Drop files here or use Add files." }));
      return;
    }
    const images = list.attachments.filter((a) => a.isImage);
    if (images.length > 0) {
      body.appendChild(imageGallery(images));
    }
    body.appendChild(el("ul", { className: "attachments__list", children: list.attachments.map((a) => fileRow(a, remove, busy)) }));
  };

  const reload = async (): Promise<void> => {
    try {
      render(await fetchAttachments(noteId, controller.signal));
    } catch (error: unknown) {
      if (isAbort(error) || disposed) {
        return;
      }
      setStatus(describeError(error), true);
    }
  };

  const remove = (attachment: Attachment): void => {
    if (busy || !window.confirm(`Remove “${attachment.filename}”? It is also deleted from Dropbox.`)) {
      return;
    }
    busy = true;
    setStatus(`Removing ${attachment.filename}…`);
    void (async (): Promise<void> => {
      try {
        await deleteAttachment(attachment.id);
        setStatus("");
      } catch (error: unknown) {
        setStatus(describeError(error), true);
      } finally {
        busy = false;
        await reload();
      }
    })();
  };

  const upload = (files: readonly File[]): void => {
    if (busy || files.length === 0 || current === null || !current.enabled) {
      return;
    }
    const maxBytes = current.maxBytes;
    busy = true;
    addButton.disabled = true;
    void (async (): Promise<void> => {
      const failures: string[] = [];
      for (const [index, file] of files.entries()) {
        if (disposed) {
          return;
        }
        if (maxBytes > 0 && file.size > maxBytes) {
          failures.push(`${file.name}: larger than ${formatBytes(maxBytes)}`);
          continue;
        }
        setStatus(`Uploading ${file.name} (${index + 1} of ${files.length})…`);
        try {
          await uploadAttachment(noteId, file);
        } catch (error: unknown) {
          failures.push(`${file.name}: ${describeError(error)}`);
        }
      }
      busy = false;
      addButton.disabled = false;
      setStatus(failures.join(" · "), failures.length > 0);
      await reload();
    })();
  };

  fileInput.addEventListener("change", () => {
    upload([...(fileInput.files ?? [])]);
    fileInput.value = "";
  });

  // Drag-and-drop anywhere on the block. preventDefault on dragover is what
  // marks it as a valid drop target; without it the browser navigates to the
  // dropped file instead.
  element.addEventListener("dragover", (event) => {
    if (event.dataTransfer?.types.includes("Files") === true) {
      event.preventDefault();
      element.classList.add("attachments--dropping");
    }
  });
  element.addEventListener("dragleave", (event) => {
    if (!element.contains(event.relatedTarget as Node | null)) {
      element.classList.remove("attachments--dropping");
    }
  });
  element.addEventListener("drop", (event) => {
    event.preventDefault();
    element.classList.remove("attachments--dropping");
    upload([...(event.dataTransfer?.files ?? [])]);
  });

  void reload();

  return {
    element,
    dispose: (): void => {
      disposed = true;
      controller.abort();
      controller = new AbortController();
    },
  };
}
